import { useMemo } from 'react';
import useNotification from './useNotification';
import { usePostsPoll } from '../queries/onboardingPolls';
import { useRegeneratePost } from '../mutations/post';
import { useUpdatePost } from '../mutations/post';
import { useGetSocialAccounts } from '../queries/social';
import { useApprovePost } from '../mutations/social';
import { useAppStore } from '../store/useAppStore';
import { usePostModalStore } from '../store/usePostModalStore';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Custom hook to manage single post logic:
 * states, queries, mutations, handlers, and effects
 * Reads post, onScheduleSuccess from postModalStore
 * @param {boolean} shouldPollPosts - Whether to enable posts polling (default: true)
 * @returns {Object} - Returns handlers and data needed by PostModal
 */
export const useSinglePost = (shouldPollPosts = true) => {
  // States
  const { accessKey } = useAppStore();
  const queryClient = useQueryClient();
  const { isOpen, post, closeModal, setPost } = usePostModalStore();
  const notification = useNotification();
  // Queries
  const {
    data: postsData,
    refetch: refetchPost,
    isLoading: isPostLoading,
  } = usePostsPoll(shouldPollPosts);
  const { data: socialAccounts = [] } = useGetSocialAccounts(accessKey);
  const regeneratedPost = useMemo(() => {
    if (!postsData || !post) return null;
    return postsData.find((p) => p.id === post.id);
  }, [postsData, post]);

  // Mutations
  const { mutate: regeneratePost } = useRegeneratePost(
    () => {
      notification.success('Post regeneration started successfully!');
      refetchPost();
      queryClient.invalidateQueries({ queryKey: ['posts-status'] });
    },
    (error) => {
      notification.error(error.message || 'Failed to regenerate post');
    }
  );
  const { mutate: updatePost } = useUpdatePost(
    () => {
      notification.success('Post updated successfully!');
      refetchPost();
      queryClient.invalidateQueries({ queryKey: ['posts-status'] });
    },
    (error) => {
      notification.error(error?.message || 'Failed to update post');
    }
  );
  const { mutate: approvePost } = useApprovePost(
    () => {
      notification.success('Post scheduled successfully!');
      closeModal();
    },
    (error) => {
      notification.error(error?.message || 'Failed to schedule post');
    }
  );

  // HandleFunctions
  const handleEditSubmit = ({ postId, data, error }) => {
    if (error) {
      notification.error(error);
      return;
    }
    if (!postId) return;
    updatePost({ postId, data });
    queryClient.invalidateQueries({ queryKey: ['posts-status'] });
  };
  const handleScheduleSubmit = (selectedSocialAccounts) => {
    if (!post?.id) return;
    approvePost({
      accessKey,
      postId: post.id,
      platforms: selectedSocialAccounts,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    queryClient.invalidateQueries({ queryKey: ['posts-status'] });
  };
  const handleRegenerateSubmit = (userInput) => {
    if (!post?.id || !userInput.trim()) return;
    regeneratePost({
      postId: post.id,
      userInput: userInput.trim(),
    });
    queryClient.invalidateQueries({ queryKey: ['posts-status'] });
  };

  // Determine if post is loading (regenerating)
  const isPostRegenerating =
    regeneratedPost?.status === 'regeneration' || post?.status === 'regeneration';

  // Return all necessary data and handlers
  return {
    postsData,
    socialAccounts,
    regeneratedPost,
    isPostLoading,
    isPostRegenerating,
    handleEditSubmit,
    handleScheduleSubmit,
    handleRegenerateSubmit,
    isOpen,
    post,
    closeModal,
    setPost,
  };
};
